import { ChannelMemberUserTypeEnum } from '@src/enum/ChannelMemberUserTypeEnum';
import logger from '@src/utils/logger';

import {
  Attachment,
  decryptData,
  encryptData,
  getDbPromise,
  THREAD_DRAFT_STORE_NAME,
  ThreadDraft,
} from './common';

export async function saveThreadDraft(
  channelCode: string,
  parentChatId: number,
  message: string,
  attachments: Attachment[] = [],
  userType?: ChannelMemberUserTypeEnum,
  userId?: number,
): Promise<void> {
  if (!channelCode || !parentChatId || !userType || !userId) {
    return;
  }
  const db = await getDbPromise();

  // SSR対応
  if (!db) {
    return;
  }

  const draft: ThreadDraft = {
    channelCode,
    parentChatId,
    message,
    attachments,
    updatedAt: Date.now(),
    userType,
    userId,
  };

  const encrypted = await encryptData(JSON.stringify(draft));
  try {
    await db.put(THREAD_DRAFT_STORE_NAME, {
      channelCode,
      userType,
      userId,
      parentChatId,
      encrypted,
    });
  } catch (error) {
    logger.error('indexedDB Save Error during database operation:', error);
  }
}

export async function loadThreadDraft(
  channelCode: string,
  parentChatId: number,
  currentUserType: ChannelMemberUserTypeEnum,
  currentUserId: number,
): Promise<ThreadDraft | undefined> {
  if (!currentUserType || !currentUserId || !parentChatId) {
    return undefined;
  }
  const db = await getDbPromise();
  if (!db) {
    return undefined;
  }

  const record = await db.get(THREAD_DRAFT_STORE_NAME, [
    channelCode,
    currentUserType,
    currentUserId,
    parentChatId,
  ]);

  if (!record || !record.encrypted || !record.userType || !record.userId) {
    return undefined;
  }
  try {
    const json = await decryptData(record.encrypted);
    return JSON.parse(json) as ThreadDraft;
  } catch (error) {
    logger.error('indexedDB Load Error:', error);
    return undefined;
  }
}

export async function deleteThreadDraft(
  channelCode: string,
  parentChatId: number,
  currentUserType: ChannelMemberUserTypeEnum,
  currentUserId: number,
): Promise<void> {
  const db = await getDbPromise();
  if (!db) {
    return;
  }

  await db.delete(THREAD_DRAFT_STORE_NAME, [
    channelCode,
    currentUserType,
    currentUserId,
    parentChatId,
  ]);
}

export async function clearThreadDrafts(): Promise<void> {
  const db = await getDbPromise();
  if (!db) {
    return;
  }

  await db.clear(THREAD_DRAFT_STORE_NAME);
}
